import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Link, useParams } from 'react-router-dom'
import { API_ORIGIN } from '@/constants'
import { ChevronDown, Copy } from 'lucide-react'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import {
  PageHeaderActionTertiary,
  PageHeaderActions,
} from '@/components/navigation/page-header-actions'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useCallEvents,
  useCallMessages,
  useCallRecord,
  useLiveCallRecordStream,
  type CallEventItem,
  type CallMessageItem,
  type CallRecordDetail,
} from '@/hooks/use-call-records'
import { cn } from '@/lib/utils'
import { formatRelativeTimeIso } from '@/lib/time-format'
import {
  CallInitialsLandmark,
  callerPrimaryLabel,
  callStoryHeroBorderClass,
  callRowSubtitle,
} from '@/components/calls/call-identity-landmark'
import { getPluginSlots } from '@/plugin/slots'

function badgeVariantForStatus(status: string) {
  if (status === 'ended' || status === 'completed') return 'secondary'
  if (status === 'in-progress' || status === 'ringing' || status === 'evaluating')
    return 'default'
  if (status === 'evaluation_failed') return 'destructive'
  return 'outline'
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return '—'
  return new Date(value).toLocaleString()
}

function formatCallHeroTime(iso: string | null | undefined) {
  if (!iso) return '—'
  return new Intl.DateTimeFormat(undefined, { timeStyle: 'short' }).format(new Date(iso))
}

function formatCallHeroDate(iso: string | null | undefined) {
  if (!iso) return null
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(iso))
}

function formatDuration(seconds?: number | null) {
  if (seconds == null) return '—'
  const total = Math.max(0, Math.floor(seconds))
  const mins = Math.floor(total / 60)
  const secs = total % 60
  if (mins <= 0) return `${secs}s`
  return `${mins}m ${secs}s`
}

function completedElapsedSeconds(data: CallRecordDetail): number | null {
  if (data.duration_seconds != null && data.duration_seconds >= 0) {
    return Math.floor(data.duration_seconds)
  }
  if (data.started_at && data.ended_at) {
    const s = new Date(data.started_at).getTime()
    const e = new Date(data.ended_at).getTime()
    if (Number.isFinite(e - s)) return Math.max(0, Math.floor((e - s) / 1000))
  }
  return null
}

function LiveCallDurationTicker({ startedAt }: { startedAt: string | null | undefined }) {
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!startedAt) return
    const id = window.setInterval(() => setTick((n) => n + 1), 1000)
    return () => window.clearInterval(id)
  }, [startedAt])

  if (!startedAt) {
    return <span className="tabular-nums">—</span>
  }
  const secs = Math.max(0, (Date.now() - new Date(startedAt).getTime()) / 1000)
  return <span className="tabular-nums">{formatDuration(Math.floor(secs))}</span>
}

async function copyText(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text)
    toast.success(`Copied ${label}`)
  } catch {
    toast.error('Copy failed')
  }
}

function CallStateBadges({ callState }: { callState: string }) {
  const s = callState.toLowerCase()
  if (s === 'emergency') {
    return (
      <Badge variant="destructive" className="font-normal shrink-0">
        Emergency
      </Badge>
    )
  }
  if (s === 'elevated') {
    return (
      <Badge variant="warningSoft" className="font-normal shrink-0">
        Elevated
      </Badge>
    )
  }
  return null
}

function CallStoryHero({
  data,
  liveStreamHint,
}: {
  data: CallRecordDetail
  liveStreamHint: ReactNode
}) {
  const callState = (data.call_state || 'routine').toLowerCase()
  const subtitle = callRowSubtitle(data)
  const { recordBadges } = getPluginSlots().calls
  const triage = data.triage_summary?.trim()
  const heroTime = formatCallHeroTime(data.started_at)
  const heroDate = formatCallHeroDate(data.started_at)
  const completedElapsed = data.is_complete ? completedElapsedSeconds(data) : null

  return (
    <Card
      className={cn(
        'rounded-lg bg-card text-card-foreground overflow-hidden py-0 gap-0',
        callStoryHeroBorderClass(callState)
      )}
    >
      <CardContent className="p-[var(--sp-card)]">
        <div className="flex items-start gap-4">
          <div className="min-w-0 flex-1 space-y-3">
            <div className="flex flex-wrap items-center gap-1.5">
              {callState === 'emergency' || callState === 'elevated' ? (
                <CallStateBadges callState={callState} />
              ) : null}
              <Badge variant={badgeVariantForStatus(data.call_status)} className="font-normal">
                {data.call_status || 'unknown'}
              </Badge>
              <Badge variant={data.is_complete ? 'secondary' : 'default'} className="font-normal">
                {data.is_complete ? 'Ended' : 'Live'}
              </Badge>
              {data.call_type ? (
                <Badge variant="outline" className="font-normal">
                  {data.call_type}
                </Badge>
              ) : null}
              <Badge variant={badgeVariantForStatus(data.evaluation_status)} className="font-normal">
                {data.evaluation_status}
              </Badge>
              {recordBadges.map((RecordBadge, index) => (
                <RecordBadge key={index} record={data} />
              ))}
              {liveStreamHint}
            </div>

            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
              {callerPrimaryLabel(data)}
            </h1>

            {subtitle ? (
              <p className="text-lg sm:text-xl text-muted-foreground leading-snug">{subtitle}</p>
            ) : null}

            <div className="space-y-1">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Triage
              </div>
              <p
                className={cn(
                  'text-base sm:text-lg leading-relaxed max-w-prose whitespace-pre-wrap',
                  triage ? 'text-foreground' : 'text-muted-foreground'
                )}
              >
                {triage || 'No triage summary yet.'}
              </p>
            </div>

            {callState === 'emergency' || callState === 'elevated' ? (
              <p className="text-xs text-muted-foreground">
                AI-assessed urgency — verify before acting.
              </p>
            ) : null}
          </div>
          <div className="flex flex-col items-end gap-3 shrink-0 text-right max-w-[11rem] sm:max-w-none">
            <CallInitialsLandmark row={data} size="hero" />
            <div className="space-y-1.5">
              <div className="space-y-0.5">
                <p className="text-lg sm:text-xl font-bold text-foreground tabular-nums tracking-tight leading-none">
                  {heroTime}
                </p>
                {heroDate ? (
                  <p className="text-sm text-muted-foreground leading-snug">{heroDate}</p>
                ) : null}
              </div>
              <div className="space-y-0.5">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {data.is_complete ? 'Elapsed' : 'Duration'}
                </p>
                <p className="text-base font-semibold text-foreground tabular-nums leading-none">
                  {data.is_complete
                    ? completedElapsed != null
                      ? formatDuration(completedElapsed)
                      : '—'
                    : (
                        <LiveCallDurationTicker startedAt={data.started_at} />
                      )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function DefinitionItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="space-y-0.5 min-w-0">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium text-foreground break-words">{value}</dd>
    </div>
  )
}

function isUserLikeRole(role: string) {
  const r = role.toLowerCase()
  return (
    r.includes('user') ||
    r.includes('customer') ||
    r.includes('caller') ||
    r === 'human'
  )
}

function TranscriptMessageBubble({ message }: { message: CallMessageItem }) {
  const role = message.role || 'unknown'
  const userLike = isUserLikeRole(role)
  const meta = [
    `#${message.sequence + 1}`,
    role,
    formatDateTime(message.event_ts || message.updated_at),
    message.is_final ? null : 'draft',
    message.source_type !== 'unknown' ? message.source_type : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <li
      className={cn(
        'flex max-w-[min(100%,42rem)] flex-col gap-1 rounded-lg px-3 py-2 text-sm',
        userLike
          ? 'ml-auto bg-primary/12 text-foreground border border-primary/20'
          : 'mr-auto bg-muted/70 text-foreground border border-border/60'
      )}
    >
      <div className="text-xs text-muted-foreground leading-tight">{meta}</div>
      <div className="whitespace-pre-wrap leading-relaxed">{message.text || '—'}</div>
      {message.turn_id ? (
        <div className="text-xs text-muted-foreground">Turn {message.turn_id}</div>
      ) : null}
    </li>
  )
}

function recordingPlaybackSrc(path: string | undefined): string {
  const trimmed = path?.trim() ?? ''
  if (!trimmed) return ''
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed
  const origin = API_ORIGIN.replace(/\/$/, '')
  return `${origin}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`
}

function payloadPreview(payload: Record<string, unknown>, max = 96) {
  const s = JSON.stringify(payload)
  if (s.length <= max) return s
  return `${s.slice(0, max)}…`
}

function toolEventName(ev: CallEventItem) {
  return ev.message_type.replace(/^tool\./, '')
}

function toolEventSummary(ev: CallEventItem) {
  const summary = ev.payload?.summary
  if (typeof summary === 'string' && summary.trim()) return summary
  return payloadPreview(ev.payload, 120)
}

export function CallRecordDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading, isError, error, refetch } = useCallRecord(id)
  const { data: messages } = useCallMessages(id)
  const { data: events } = useCallEvents(id)
  const { recordPanels } = getPluginSlots().calls
  const liveDetailStream = useLiveCallRecordStream(id, !data?.is_complete)
  const transcriptScrollRef = useRef<HTMLDivElement | null>(null)
  const [expandedEventIds, setExpandedEventIds] = useState<Record<string, boolean>>({})
  const [playbackSrc, setPlaybackSrc] = useState('')
  const playbackRefreshed = useRef(false)

  useEffect(() => {
    playbackRefreshed.current = false
    setPlaybackSrc('')
  }, [id])

  useEffect(() => {
    const next = recordingPlaybackSrc(data?.recording_playback_url)
    setPlaybackSrc((current) => {
      if (!next) return ''
      if (current) return current
      return next
    })
  }, [data?.recording_playback_url, id])

  const sortedMessages = useMemo(() => {
    if (!messages?.length) return []
    return [...messages].sort((a, b) => a.sequence - b.sequence)
  }, [messages])

  const sortedEvents = useMemo(() => {
    if (!events?.length) return []
    return [...events].sort(
      (a, b) =>
        new Date(b.received_at).getTime() - new Date(a.received_at).getTime()
    )
  }, [events])

  const toolEvents = useMemo(
    () =>
      (events ?? [])
        .filter((ev) => ev.message_type.startsWith('tool.'))
        .sort(
          (a, b) =>
            new Date(a.received_at).getTime() - new Date(b.received_at).getTime()
        ),
    [events]
  )

  const errMsg = error instanceof Error ? error.message : 'Something went wrong'
  const isNotFound = errMsg === 'Not found'

  const isLiveCall = !!(data && !data.is_complete)
  const showPostCallSummarySection =
    !!data && (data.is_complete || Boolean(data.summary?.trim()))
  const showEvaluationSection =
    !!data && (data.is_complete || Boolean(data.evaluation_summary?.trim()))

  useLayoutEffect(() => {
    const el = transcriptScrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [sortedMessages, data?.transcript])

  function toggleEventExpand(eventId: string) {
    setExpandedEventIds((prev) => ({ ...prev, [eventId]: !prev[eventId] }))
  }

  const liveStreamHint =
    data &&
    !data.is_complete &&
    (liveDetailStream.status === 'open' ||
      liveDetailStream.status === 'connecting' ||
      liveDetailStream.status === 'reconnecting') ? (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <span
          className={cn(
            'size-1.5 rounded-full shrink-0',
            liveDetailStream.status === 'open' && 'bg-success',
            liveDetailStream.status === 'connecting' && 'bg-warning animate-pulse',
            liveDetailStream.status === 'reconnecting' && 'bg-warning animate-pulse'
          )}
          aria-hidden
        />
        {liveDetailStream.status === 'open' ? 'Live sync' : 'Connecting…'}
        {liveDetailStream.status === 'open' && liveDetailStream.lastEventAt ? (
          <span className="tabular-nums">
            · updated{' '}
            {formatRelativeTimeIso(
              new Date(liveDetailStream.lastEventAt).toISOString()
            )}
          </span>
        ) : null}
      </span>
    ) : null

  return (
    <PageContainer>
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <PageHeader
          leading={
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
              <PageHeaderLeading back={{ label: 'Back to live calls', to: '/calls/records' }} />
              {!data && isLoading ? (
                <div className="flex flex-1 items-center gap-2 min-w-0">
                  <Skeleton className="h-8 w-40" />
                  <Skeleton className="h-6 w-24 rounded-full" />
                </div>
              ) : null}
            </div>
          }
          actions={
            data ? (
              <PageHeaderActions
                tertiary={
                  <div className="flex items-center gap-1 min-w-0 max-w-[min(100%,280px)] sm:max-w-[min(100%,420px)]">
                    <code className="truncate font-mono text-xs text-muted-foreground">
                      {data.vapi_call_id}
                    </code>
                    <PageHeaderActionTertiary
                      aria-label="Copy call id"
                      onClick={() => void copyText(data.vapi_call_id, 'call id')}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </PageHeaderActionTertiary>
                  </div>
                }
              />
            ) : null
          }
        />
      </div>

      {isError && !data ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {isNotFound ? 'Call record not found' : 'Could not load record'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {isNotFound
                ? 'This id may be invalid or the record was removed.'
                : errMsg}
            </p>
            <div className="flex flex-wrap gap-2">
              {!isNotFound ? (
                <Button type="button" size="sm" onClick={() => void refetch()}>
                  Retry
                </Button>
              ) : null}
              <Button variant="outline" size="sm" asChild>
                <Link to="/calls/records">Back to live calls</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {isLoading && !data && !isError ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-full max-w-md" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : null}

      {data && (
        <>
          <CallStoryHero data={data} liveStreamHint={liveStreamHint} />

          <Card>
            <CardContent>
              <div
                ref={transcriptScrollRef}
                className="max-h-[min(70vh,900px)] overflow-y-auto pr-1"
              >
                {sortedMessages.length > 0 ? (
                  <ol className="flex flex-col gap-3 list-none p-0 m-0">
                    {sortedMessages.map((message) => (
                      <TranscriptMessageBubble key={message.id} message={message} />
                    ))}
                  </ol>
                ) : null}
                {data.transcript?.trim() ? (
                  <div className={sortedMessages.length > 0 ? 'mt-4' : undefined}>
                    {sortedMessages.length > 0 ? (
                      <div className="mb-2 text-xs font-medium text-foreground">Full transcript</div>
                    ) : null}
                    <pre className="text-sm whitespace-pre-wrap font-sans text-muted-foreground leading-relaxed">{data.transcript}</pre>
                  </div>
                ) : sortedMessages.length === 0 ? (
                  <pre className="text-sm whitespace-pre-wrap font-sans text-muted-foreground leading-relaxed">
                    —
                  </pre>
                ) : null}
              </div>
            </CardContent>
          </Card>

          {recordPanels.map((RecordPanel, index) => (
            <RecordPanel key={index} record={data} />
          ))}

          <Card>
            <CardHeader>
              <CardTitle>{isLiveCall ? 'Call details (live)' : 'Call details'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <dl className="grid gap-4 sm:grid-cols-2">
                <DefinitionItem label="Assistant" value={data.assistant_name || '—'} />
                {data.call_type ? (
                  <DefinitionItem label="Call type" value={data.call_type} />
                ) : null}
                <DefinitionItem label="Messages" value={String(data.message_count)} />
                <DefinitionItem label="Started" value={formatDateTime(data.started_at)} />
                <DefinitionItem label="Ended" value={formatDateTime(data.ended_at)} />
                <DefinitionItem label="Duration" value={formatDuration(data.duration_seconds)} />
                <DefinitionItem label="Ended reason" value={data.ended_reason || '—'} />
                <DefinitionItem
                  label="Last event"
                  value={
                    <>
                      {data.last_event_type || '—'}
                      {data.last_event_at ? (
                        <span className="block text-xs font-normal text-muted-foreground mt-0.5">
                          {formatDateTime(data.last_event_at)}
                        </span>
                      ) : null}
                    </>
                  }
                />
              </dl>

              <div className="space-y-3 text-sm text-muted-foreground">
                <h3 className="text-sm font-semibold text-foreground">Latest activity</h3>
                <div>
                  <div className="font-medium text-foreground text-xs mb-1">Most recent</div>
                  <div>
                    {data.last_message_text
                      ? `${data.last_message_role || 'speaker'}: ${data.last_message_text}`
                      : '—'}
                  </div>
                </div>
                {data.live_transcript_preview ? (
                  <div className="rounded-md border border-dashed border-border/80 p-4 bg-muted/20">
                    <div className="font-medium text-foreground text-xs mb-1">
                      Live {data.live_transcript_role || 'speaker'}
                    </div>
                    <div className="leading-relaxed">{data.live_transcript_preview}</div>
                  </div>
                ) : null}
              </div>

              {playbackSrc ? (
                <div className="space-y-3">
                  <div className="flex flex-row flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-foreground">Recording</h3>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void copyText(playbackSrc, 'recording URL')}
                    >
                      <Copy className="h-3.5 w-3.5 mr-1" />
                      Copy URL
                    </Button>
                  </div>
                  <audio
                    controls
                    className="w-full max-w-lg"
                    src={playbackSrc}
                    onError={() => {
                      if (playbackRefreshed.current) return
                      playbackRefreshed.current = true
                      void refetch().then((result) => {
                        const next = recordingPlaybackSrc(result.data?.recording_playback_url)
                        if (next) setPlaybackSrc(next)
                      })
                    }}
                  />
                  <a
                    href={playbackSrc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary underline underline-offset-2"
                  >
                    Open in new tab
                  </a>
                </div>
              ) : null}

              {showPostCallSummarySection ? (
                <div className="space-y-3">
                  <div className="flex flex-row flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-foreground">Post-call summary</h3>
                    {data.summary?.trim() ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void copyText(data.summary, 'summary')}
                      >
                        <Copy className="h-3.5 w-3.5 mr-1" />
                        Copy
                      </Button>
                    ) : null}
                  </div>
                  <div className="text-sm leading-relaxed text-muted-foreground max-w-prose whitespace-pre-wrap">
                    {data.summary?.trim() || '—'}
                  </div>
                </div>
              ) : null}

              {showEvaluationSection ? (
                <div className="space-y-3">
                  <div className="flex flex-row flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-foreground">Evaluation</h3>
                    {data.evaluation_summary?.trim() ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void copyText(data.evaluation_summary, 'evaluation')}
                      >
                        <Copy className="h-3.5 w-3.5 mr-1" />
                        Copy
                      </Button>
                    ) : null}
                  </div>
                  <div className="text-sm leading-relaxed text-muted-foreground max-w-prose whitespace-pre-wrap">
                    {data.evaluation_summary?.trim() || '—'}
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Agent tools</CardTitle>
            </CardHeader>
            <CardContent>
              {toolEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {events === undefined
                    ? 'Loading…'
                    : 'No scheduling tool calls recorded for this call.'}
                </p>
              ) : (
                <ol className="space-y-3">
                  {toolEvents.map((ev) => (
                    <li key={ev.id} className="text-sm">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <span className="font-medium text-foreground">
                          {toolEventName(ev)}
                        </span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {formatDateTime(ev.received_at)}
                        </span>
                      </div>
                      <p className="text-muted-foreground mt-0.5">{toolEventSummary(ev)}</p>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>

          <details className="group rounded-lg border border-border bg-card text-card-foreground overflow-hidden">
            <summary className="cursor-pointer select-none list-none px-4 py-3 text-sm font-medium border-b border-border flex items-center gap-2 [&::-webkit-details-marker]:hidden">
              <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" />
              Technical details
            </summary>
            <div className="p-4 space-y-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle>Provider context</CardTitle>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      void copyText(
                        JSON.stringify(data.provider_context || {}, null, 2),
                        'provider context'
                      )
                    }
                  >
                    <Copy className="h-3.5 w-3.5 mr-1" />
                    Copy JSON
                  </Button>
                </CardHeader>
                <CardContent>
                  <pre className="text-xs whitespace-pre-wrap font-mono text-muted-foreground max-h-[480px] overflow-y-auto rounded-md border bg-muted/20 p-4">
                    {JSON.stringify(data.provider_context || {}, null, 2)}
                  </pre>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Provider events</CardTitle>
                </CardHeader>
                <CardContent>
                  {!events?.length ? (
                    <p className="text-sm text-muted-foreground">
                      {events === undefined ? 'Loading…' : 'No stored provider events.'}
                    </p>
                  ) : (
                    <div className="max-h-[420px] overflow-auto rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[140px]">Received</TableHead>
                            <TableHead className="w-[120px]">Type</TableHead>
                            <TableHead className="w-[100px]">Turn</TableHead>
                            <TableHead>Payload</TableHead>
                            <TableHead className="w-[52px] text-right pr-2" />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sortedEvents.map((ev: CallEventItem) => {
                            const open = !!expandedEventIds[ev.id]
                            return (
                              <Fragment key={ev.id}>
                                <TableRow>
                                  <TableCell className="text-xs font-mono whitespace-nowrap align-top">
                                    {formatDateTime(ev.received_at)}
                                  </TableCell>
                                  <TableCell className="text-xs align-top">{ev.message_type}</TableCell>
                                  <TableCell className="text-xs align-top font-mono">
                                    {ev.turn_id || '—'}
                                  </TableCell>
                                  <TableCell className="text-xs font-mono align-top max-w-[240px]">
                                    <span className="break-all text-muted-foreground">
                                      {payloadPreview(ev.payload)}
                                    </span>
                                  </TableCell>
                                  <TableCell className="align-top text-right p-1">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      aria-expanded={open}
                                      aria-label={open ? 'Collapse JSON' : 'Expand JSON'}
                                      onClick={() => toggleEventExpand(ev.id)}
                                    >
                                      <ChevronDown
                                        className={cn(
                                          'h-4 w-4 transition-transform',
                                          open && 'rotate-180'
                                        )}
                                      />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                                {open ? (
                                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                                    <TableCell colSpan={5} className="p-4">
                                      <pre className="text-xs whitespace-pre-wrap font-mono text-muted-foreground max-h-[280px] overflow-y-auto">
                                        {JSON.stringify(ev.payload, null, 2)}
                                      </pre>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="mt-2"
                                        onClick={() =>
                                          void copyText(
                                            JSON.stringify(ev.payload, null, 2),
                                            'event payload'
                                          )
                                        }
                                      >
                                        <Copy className="h-3.5 w-3.5 mr-1" />
                                        Copy payload
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ) : null}
                              </Fragment>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </details>
        </>
      )}
    </PageContainer>
  )
}
